import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from 'react';
/** Même calcul que KbDocumentParser::anchor() côté backend. */
export function headingAnchor(heading) {
    return heading.trim().toLowerCase().replace(/[^\p{L}\p{N}\s_-]/gu, '').replace(/\s/gu, '-');
}
export function SafeMarkdown({ text, onCite, citable, anchors }) {
    const inline = (s, key) => renderInline(s, key, onCite, citable);
    const blocks = [];
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const key = `b${i}`;
        if (line.trim() === '') {
            i++;
            continue;
        }
        if (line.trim().startsWith('```')) {
            const code = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith('```'))
                code.push(lines[i++]);
            i++;
            blocks.push(_jsx("pre", { className: "bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap", children: code.join('\n') }, key));
            continue;
        }
        const h = /^(#{1,4})\s+(.+?)\s*#*\s*$/.exec(line);
        if (h) {
            const level = h[1].length;
            const cls = level <= 1 ? 'text-lg font-semibold' : level === 2 ? 'text-base font-semibold mt-2' : 'text-sm font-semibold mt-1';
            const Tag = (`h${Math.min(level + 1, 6)}`);
            blocks.push(_jsx(Tag, { id: anchors ? headingAnchor(h[2]) : undefined, className: `${cls} text-gray-900 scroll-mt-4`, children: inline(h[2], key) }, key));
            i++;
            continue;
        }
        if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
            const cells = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
            const head = cells(line);
            i += 2;
            const rows = [];
            while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i]))
                rows.push(cells(lines[i++]));
            blocks.push(_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "text-xs border-collapse w-full", children: [_jsx("thead", { children: _jsx("tr", { children: head.map((c, j) => _jsx("th", { className: "border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold", children: inline(c, `${key}h${j}`) }, j)) }) }), _jsx("tbody", { children: rows.map((r, ri) => _jsx("tr", { children: r.map((c, j) => _jsx("td", { className: "border border-gray-200 px-2 py-1 align-top", children: inline(c, `${key}r${ri}c${j}`) }, j)) }, ri)) })] }) }, key));
            continue;
        }
        if (/^\s*>/.test(line)) {
            const quote = [];
            while (i < lines.length && /^\s*>/.test(lines[i]))
                quote.push(lines[i++].replace(/^\s*>\s?/, ''));
            blocks.push(_jsx("blockquote", { className: "border-l-4 border-gray-200 pl-3 text-gray-600", children: inline(quote.join(' '), key) }, key));
            continue;
        }
        const ordered = /^\s*\d+[.)]\s+/;
        const bullet = /^\s*[-*•]\s+/;
        if (ordered.test(line) || bullet.test(line)) {
            const isOrdered = ordered.test(line);
            const marker = isOrdered ? ordered : bullet;
            const items = [];
            while (i < lines.length && (marker.test(lines[i]) || (/^\s{2,}\S/.test(lines[i]) && items.length > 0))) {
                if (marker.test(lines[i]))
                    items.push(lines[i].replace(marker, ''));
                else
                    items[items.length - 1] += ' ' + lines[i].trim();
                i++;
            }
            const List = isOrdered ? 'ol' : 'ul';
            blocks.push(_jsx(List, { className: `${isOrdered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1`, children: items.map((it, j) => _jsx("li", { children: inline(it, `${key}i${j}`) }, j)) }, key));
            continue;
        }
        const para = [];
        while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4}\s|```|\s*>|\s*\d+[.)]\s|\s*[-*•]\s|\s*\|)/.test(lines[i])) {
            para.push(lines[i++].trim());
        }
        if (para.length === 0) {
            para.push(line.trim());
            i++;
        }
        blocks.push(_jsx("p", { children: inline(para.join(' '), key) }, key));
    }
    return _jsx("div", { className: "space-y-2 break-words", children: blocks });
}
function renderInline(text, key, onCite, citable) {
    // Liens Markdown : texte seul, l'adresse n'est jamais rendue cliquable.
    const s = text.replace(/\[([^\]]+)\]\((?:[^)]+)\)/g, '$1');
    const parts = [];
    const re = /(\*\*[^*]+\*\*|`[^`]+`|\[\d{1,2}\])/g;
    let last = 0;
    let m;
    let k = 0;
    while ((m = re.exec(s)) !== null) {
        if (m.index > last)
            parts.push(s.slice(last, m.index));
        const tok = m[0];
        if (tok.startsWith('**')) {
            parts.push(_jsx("strong", { className: "font-semibold", children: tok.slice(2, -2) }, `${key}-${k++}`));
        }
        else if (tok.startsWith('`')) {
            parts.push(_jsx("code", { className: "bg-gray-100 rounded px-1 text-[0.85em]", children: tok.slice(1, -1) }, `${key}-${k++}`));
        }
        else {
            const n = Number(tok.slice(1, -1));
            if (onCite && (!citable || citable.includes(n))) {
                parts.push(_jsx("button", { type: "button", onClick: () => onCite(n), className: "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 mx-0.5 align-baseline rounded bg-blue-50 text-blue-700 text-[11px] font-semibold hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400", "aria-label": `Source ${n}`, children: n }, `${key}-${k++}`));
            }
            else {
                parts.push(tok);
            }
        }
        last = m.index + tok.length;
    }
    if (last < s.length)
        parts.push(s.slice(last));
    return _jsx(Fragment, { children: parts }, key);
}
